// In-memory dashboard data cache. Used by useWorkerDashboardData,
// useSupervisorDashboardData, and useAdminDerivedData to skip the
// 9-query Promise.all on navigation when fresh data exists.
//
// Security model:
//   - Key includes tenantId + userId + kind. Cross-user / cross-tenant
//     leakage impossible by construction. Role-routed reads (supervisor /
//     worker: base tables with dollars vs the dollar-free `field_*` views)
//     use the VARIANT api below: the key also carries the table set the data
//     came from, and any read/write purges the sibling variants + the legacy
//     un-variant key, so a priced entry can't outlive a price-visibility flip.
//   - Memory-only (no localStorage / IndexedDB) — defends against
//     shared-machine session leakage.
//   - Cleared on signOut() in AuthContext.
//   - Never store the session, JWT, or anything that grants access —
//     only data the authenticated query already returned.
//
// Performance / iOS-readiness:
//   - LRU bounded at MAX_ENTRIES (50) — caps memory at ~10MB worst case,
//     well under iOS memory pressure threshold.
//   - TTL = 5 min — short enough that stale data isn't a real risk
//     for ops, long enough to span typical Dashboard ↔ LogJob round trips.
//   - Passive expiration on read (no setInterval / no GC pressure).
//   - LRU eviction relies on Map's insertion-order iteration (ES2015+).

const TTL_MS = 5 * 60 * 1000;
const MAX_ENTRIES = 50;

// Map<string, { data: unknown, at: number }>
const cache = new Map();

// Test seam — overridden via __setNowForTests.
let nowFn = () => Date.now();

function keyOf(tenantId, userId, kind) {
  if (!tenantId || !userId || !kind) return null;
  return `${tenantId}:${userId}:${kind}`;
}

export function getCachedDashboard(tenantId, userId, kind) {
  const key = keyOf(tenantId, userId, kind);
  if (!key) return null;
  const entry = cache.get(key);
  if (!entry) return null;
  if (nowFn() - entry.at > TTL_MS) {
    cache.delete(key);
    return null;
  }
  // Touch — re-insert to push to the most-recent end of insertion order.
  cache.delete(key);
  cache.set(key, entry);
  return entry.data;
}

export function setCachedDashboard(tenantId, userId, kind, data) {
  const key = keyOf(tenantId, userId, kind);
  if (!key) return;
  // Evict the oldest entry if at cap and this would be a new insertion.
  if (!cache.has(key) && cache.size >= MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
  }
  cache.delete(key);
  cache.set(key, { data, at: nowFn() });
}

// KAN-982 — variant-keyed entries for ROLE-ROUTED reads. Every call first
// deletes every other entry of the same tenant+user+kind (other variants and
// the legacy un-variant key). Fails closed: any missing part → no read/write.
function purgeOtherVariants(tenantId, userId, kind, variant) {
  const base = `${tenantId}:${userId}:${kind}`;
  const keep = `${base}:${variant}`;
  const prefix = `${base}:`;
  for (const k of Array.from(cache.keys())) {
    if (k !== keep && (k === base || k.startsWith(prefix))) cache.delete(k);
  }
  return keep;
}

/**
 * KAN-982 — drop every cached entry of this tenant+user+kind except
 * `keepVariant`'s (and the legacy un-variant key). Fails closed.
 */
export function purgeDashboardVariants(tenantId, userId, kind, keepVariant) {
  if (!tenantId || !userId || !kind || !keepVariant) return;
  purgeOtherVariants(tenantId, userId, kind, keepVariant);
}

// Money fields a job / line-item row can carry; the dollar-free variant never holds them.
export function stripJobMoney(job) {
  if (!job || typeof job !== 'object') return job;
  const { total, ...rest } = job; // eslint-disable-line no-unused-vars
  return rest;
}
export function stripLineItemMoney(li) {
  if (!li || typeof li !== 'object') return li;
  const { unit_price, total, line_total, ...rest } = li; // eslint-disable-line no-unused-vars
  return rest;
}
export function stripLineItemsMapMoney(map) {
  const out = {};
  Object.keys(map || {}).forEach((k) => {
    out[k] = Array.isArray(map[k]) ? map[k].map(stripLineItemMoney) : map[k];
  });
  return out;
}

export function getCachedDashboardVariant(tenantId, userId, kind, variant) {
  if (!tenantId || !userId || !kind || !variant) return null;
  const key = purgeOtherVariants(tenantId, userId, kind, variant);
  const entry = cache.get(key);
  if (!entry) return null;
  if (nowFn() - entry.at > TTL_MS) {
    cache.delete(key);
    return null;
  }
  cache.delete(key);
  cache.set(key, entry);
  return entry.data;
}

export function setCachedDashboardVariant(tenantId, userId, kind, variant, data) {
  if (!tenantId || !userId || !kind || !variant) return;
  const key = purgeOtherVariants(tenantId, userId, kind, variant);
  if (!cache.has(key) && cache.size >= MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
  }
  cache.delete(key);
  cache.set(key, { data, at: nowFn() });
}

export function clearAllDashboardCache() {
  cache.clear();
}

/**
 * Invalidate every cached entry for a (tenant, user). Used as a fallback
 * after a mutation when an in-place update isn't practical. Triggers a
 * cold load + skeleton on next dashboard mount.
 *
 * Doesn't touch other users' entries — safe to call from a single-user
 * context. No-op if tenant/user not provided.
 */
export function invalidateDashboardCacheForUser(tenantId, userId) {
  if (!tenantId || !userId) return;
  const prefix = `${tenantId}:${userId}:`;
  for (const k of cache.keys()) {
    if (k.startsWith(prefix)) cache.delete(k);
  }
}

/**
 * Append a freshly-inserted job (and its line items) to every cached
 * dashboard entry for this user. Lets the dashboard render the new job
 * IMMEDIATELY on remount with no skeleton flash AND no stale flicker.
 *
 * The full silent refetch still runs on remount, so any server-side
 * derivations we don't compute here (totals, aggregates) self-correct
 * within ~200-500ms.
 *
 * Inputs:
 *   - tenantId, userId — partition the update to this user only
 *   - job — the freshly-inserted jobs row (must include id, customer_id,
 *     worker_id, date, etc.)
 *   - lineItems — the inserted job_line_items rows (must include job_id,
 *     type, catalog_id, name, unit_price, quantity)
 *
 * Updates ALL cache entries for the user (worker / supervisor / admin
 * dashboards if any are warm) since we can't know which role mounted
 * the LogJob page. Each kind owns its own jobs[] + lineItemsByJobId
 * under the same shape so the patch logic is uniform.
 */
export function patchDashboardCacheWithJob(tenantId, userId, job, lineItems) {
  if (!tenantId || !userId || !job || !job.id) return;
  const prefix = `${tenantId}:${userId}:`;
  for (const [key, entry] of cache.entries()) {
    if (!key.startsWith(prefix)) continue;
    const data = entry.data || {};
    // Dollar-free variant (field_* tables): never patch money into it.
    const blindEntry = key.includes(':field_jobs|');
    const jobIn = blindEntry ? stripJobMoney(job) : job;
    const itemsIn = blindEntry && Array.isArray(lineItems) ? lineItems.map(stripLineItemMoney) : lineItems;
    const prevJobs = Array.isArray(data.jobs) ? data.jobs : [];
    // Skip if job already in cache (idempotent)
    if (prevJobs.some((j) => j && j.id === job.id)) continue;
    const prevLineItems = data.lineItemsByJobId && typeof data.lineItemsByJobId === 'object'
      ? data.lineItemsByJobId
      : {};
    cache.set(key, {
      data: {
        ...data,
        jobs: [...prevJobs, jobIn],
        lineItemsByJobId: {
          ...prevLineItems,
          [job.id]: Array.isArray(itemsIn) ? itemsIn : [],
        },
      },
      at: entry.at, // preserve original timestamp — this is a derivative, not a fresh fetch
    });
  }
}

// ── Test seams (NOT for production callers) ──────────────────────────────
export function __setNowForTests(ms) { nowFn = () => ms; }
export function __resetForTests() {
  cache.clear();
  nowFn = () => Date.now();
}
